import type { PoiDataGrid } from "@/threads/DataProcessingThread/types";
import type { Area, CoordinateSystem } from "@mm/api.meteomatics.com";

export const POI_BOUNDARY_STATE_PROPERTY = "poi-boundary-state";
export const POI_ORIGINAL_INDEX_PROPERTY = "poi-original-index";
export const POI_WITHIN_BOUNDS_STATE = "within-bounds";
export const POI_OUT_OF_BOUNDS_STATE = "out-of-bounds";
export const POI_INVALID_VALUE_STATE = "invalid-value";
export const POI_OUT_OF_BOUNDS_COLOR = "#61646B";
export const POI_OUT_OF_BOUNDS_OPACITY_MULTIPLIER = 0.4;
export const POI_INVALID_RESPONSE_VALUE = -999;

/**
 * Splits the configured PoI list into the subset that may be requested from the API and the subset that
 * must stay as local fallback markers because they lie outside the current model boundary.
 */
export type PoiBoundaryPartition = {
  originalCoords: PoiDataGrid;
  inBoundsCoords: PoiDataGrid;
  inBoundsIndexes: number[];
  outOfBoundsIndexes: number[];
};

/**
 * Checks a single PoI against an axis-aligned model boundary in WGS84 space.
 */
export function isPoiCoordinateWithinBoundary(
  coord: PoiDataGrid[number],
  boundary: Area<CoordinateSystem.WGS84>,
): boolean {
  const lat = Number(coord.lat);
  const lon = Number(coord.lon);

  return lat >= boundary.south && lat <= boundary.north && lon >= boundary.west && lon <= boundary.east;
}

/**
 * Preserves the original coordinate order while preparing the requestable subset and the original indexes
 * needed to merge API responses back onto the full rendered PoI list.
 */
export function partitionPoiCoordsByBoundary(
  coords: PoiDataGrid,
  boundary: Area<CoordinateSystem.WGS84>,
): PoiBoundaryPartition {
  const inBoundsCoords: PoiDataGrid = [];
  const inBoundsIndexes: number[] = [];
  const outOfBoundsIndexes: number[] = [];

  coords.forEach((coord, index) => {
    if (isPoiCoordinateWithinBoundary(coord, boundary)) {
      inBoundsCoords.push(coord);
      inBoundsIndexes.push(index);
      return;
    }

    outOfBoundsIndexes.push(index);
  });

  return {
    originalCoords: coords,
    inBoundsCoords,
    inBoundsIndexes,
    outOfBoundsIndexes,
  };
}

/**
 * Returns whether a rendered PoI should use the neutral grey fallback styling.
 */
export function isPoiGreyedOutState(state: unknown): boolean {
  return state === POI_OUT_OF_BOUNDS_STATE || state === POI_INVALID_VALUE_STATE;
}
